// src/App.tsx - Updated with Analytics
import { useEffect } from 'react';
import { Route, Switch, BrowserRouter, Redirect } from 'react-router-dom';
import Header from './Header';
import './App.scss';
import './firebase/config';

// Analytics
import AnalyticsWrapper from './components/AnalyticsWrapper';
import { usePageTracking, useScrollTracking, useVisibilityTracking } from './hooks/useAnalytics';

// Pages
import Signup from './pages/Signup';
import Login from './pages/Login';
import { UserProvider } from './firebase/UserProvider';
import Profile from './pages/Profile';
import ProviderProfile from './pages/ProviderProfile';
import AddReading from './pages/AddReading';
import ListReading from './pages/ListReadings';
import Report from './pages/Report';
import MetricAvgReadings from './pages/MetricAvgReadings';
import MetricGender from './pages/MetricGender';
import MetricGenderAge from './pages/MetricGenderAge';
import Override from './pages/Override';
import ExportReadings from './pages/ExportReadings';
import MetricBaselineReadings from './pages/MetricBaselineReadings';
import Samples from './pages/Samples';
import Notifications from './pages/Notifications';
import ProfileRedirect from './router/ProfileRedirect';
import PrivateRoute from './router/PrivateRoute';
import AdminRoute from './router/AdminRoute';
import DoctorRoute from './router/DoctorRoute';
import Users from './pages/Users';
import ListPatients from './pages/ListPatients';
import ListDoctorsPatients from './pages/ListDoctorsPatients';
import Patient from './pages/Patient';
import JoinForm from './components/JoinForm';
import packageJson from '../package.json';
import { getBuildDate } from './utils/utils';
import withClearCache from './ClearCache';
import PasswordReset from './components/PasswordReset';
import ListRegistrations from './pages/ListRegistrations';

const ClearCacheComponent = withClearCache(MainApp);

function App() {
  return (
    <UserProvider>
      <BrowserRouter>
        <AnalyticsWrapper>
          <ClearCacheComponent />
        </AnalyticsWrapper>
      </BrowserRouter>
    </UserProvider>
  );
}

function browserTypeVersion(): string {
  return [
    navigator?.userAgent,
    navigator?.appName,
    navigator?.appVersion,
  ]
    .filter(Boolean)
    .join(' ');
}

let deferredPrompt: any;

window.addEventListener('appinstalled', () => {
  console.log('my.SmartViewMD.com [Added to HomeScreen]');
  deferredPrompt = null;
  console.log('PWA was installed');
});

window.addEventListener('beforeinstallprompt', (event) => {
  console.log('my.SmartViewMD.com ... [Add to HomeScreen] feature ready ...');
  event.preventDefault();
  deferredPrompt = event;

  const installBtn = document.querySelector('#installBtn');
  if (installBtn) {
    installBtn.addEventListener('click', () => {
      if (deferredPrompt !== null) {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.then((choiceResult: any) => {
          if (choiceResult.outcome === 'accepted') {
            console.log('User accepted the A2HS prompt');
          } else {
            console.log('User dismissed the A2HS prompt');
          }
          deferredPrompt = null;
        });
      }
    });
  }

  const installBanner = document.querySelector('#installBanner') as HTMLElement;
  if (installBanner) {
    installBanner.style.display = 'flex';
  }
});

interface MainAppProps {
  isLatestVersion?: boolean;
  emptyCacheStorage?: () => void;
}

function MainApp({ isLatestVersion, emptyCacheStorage }: MainAppProps) {
  // Enable automatic page tracking
  usePageTracking();
  useScrollTracking();
  useVisibilityTracking();

  useEffect(() => {
    // Warm up OCR endpoints
    const warmupEndpoint = async (url: string) => {
      try {
        const response = await fetch(url, {
          method: 'OPTIONS',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({ data: '1' }),
        });
        const data = await response.text();
        console.log('Response:', data);
      } catch (error) {
        console.error('Error:', error);
      }
    };

    warmupEndpoint('https://sharprpm-ocr-test4-850861419930.us-central1.run.app');
    warmupEndpoint('https://sharprpm-ocr-scale-test1-850861419930.us-central1.run.app');
  }, []);

  const VERSION = packageJson.version;
  console.log(process.env.NODE_ENV, VERSION);

  const removeAll = () => {
    window.localStorage.clear();
    window.sessionStorage.clear();
    
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
      window.location.reload();
    }
    
    alert('Cleared Storage & Cache');
  };

  const InstallApp = () => {
    if (deferredPrompt == null) {
      alert(
        `From many supported modern web browsers, you can install this my.SmartViewMD.com app. On the Google Chrome menu of an Android phone, select [Add to Home screen]. Similarly on an iPhone, the web browser provided a download icon (at bottom of initial page launch) or menu to [Add to Home screen] as shown in our BLOG video "How to set up SmartViewMD Application" from www.SmartViewMD.com/V1 ... ${browserTypeVersion()}`
      );
    } else {
      console.log('Prompting for A2HS...');
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the A2HS prompt');
        } else {
          console.log('User dismissed the A2HS prompt');
        }
        deferredPrompt = null;
      });
    }
  };

  return (
    <>
      <Header />
      <div className="app">
        <div className="ui grid container">
          <Switch>
            <ProfileRedirect exact path="/signup" component={Signup} />
            <PrivateRoute exact path="/pprofile/:id" component={ProviderProfile} />
            <PrivateRoute exact path="/profile/:id" component={Profile} />
            <PrivateRoute exact path="/password/:id" component={JoinForm} />
            <PrivateRoute exact path="/reading/:id" component={AddReading} />
            <PrivateRoute
              exact
              path="/readings/:id/:name?"
              component={ListReading}
            />
            <PrivateRoute
              exact
              path="/reports/:id?/:name?"
              component={Report}
            />
            <PrivateRoute
              exact
              path="/avgReads/:interval?/:duration?"
              component={MetricAvgReadings}
            />
            <PrivateRoute
              exact
              path="/genderReport"
              component={MetricGender}
            />
            <PrivateRoute
              exact
              path="/genderAgeReport"
              component={MetricGenderAge}
            />
            <PrivateRoute exact path="/override" component={Override} />
            <PrivateRoute
              exact
              path="/exportReadings"
              component={ExportReadings}
            />
            <PrivateRoute
              exact
              path="/baselineReads/:interval?/:duration?"
              component={MetricBaselineReadings}
            />
            <PrivateRoute
              exact
              path="/samples/:id?/:device?"
              component={Samples}
            />
            <PrivateRoute
              exact
              path="/notify/:id?/:device?"
              component={Notifications}
            />
            <ProfileRedirect exact path="/login" component={Login} />
            <ProfileRedirect exact path="/resetpw" component={PasswordReset} />
            <AdminRoute exact path="/users/:type?" component={Users} />
            <DoctorRoute exact path="/patients/:type?" component={ListPatients} />
            <DoctorRoute
              exact
              path="/doctors/patients/:type?"
              component={ListDoctorsPatients}
            />
            <DoctorRoute
              exact
              path="/listRegistrations"
              component={ListRegistrations}
            />
            <PrivateRoute exact path="/patient/:id/:name?" component={Patient} />
            <Route exact path="/">
              <Redirect to="/login" />
            </Route>
          </Switch>
        </div>
      </div>
      <div className="centeredDiv">
        <button onClick={removeAll}>
          v{VERSION} {getBuildDate(packageJson.buildDate)}
        </button>
        <div style={{ marginLeft: '.9rem' }}>
          {browserTypeVersion().includes('Android') && (
            <button onClick={InstallApp} id="installBtn">
              Install App
            </button>
          )}
        </div>
      </div>
      <div className="centeredDiv2">
        Powered by{' '}
        <div style={{ marginLeft: '.3rem' }}>
          <a href="http://SmartViewMD.com">SmartViewMD</a>
        </div>
      </div>
    </>
  );
}

export default App;